import { SearchRounded } from "@mui/icons-material";
import {
    Box,
    InputAdornment,
    Radio,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import React, { useEffect, useMemo, useState } from "react";
import {
    KboFlowDebugService,
    KboFlowStepDebugRun,
} from "services/KboFlowDebugService";
import { UserForSharing } from "services/UserService";
import { shortId } from "../KboFlowDebug/KboFlowDebugUI";
import { formatRunDate } from "../KboFlowRuns/KboFlowRunDigestModels";
import { FlowStep, RUN_DATE_WINDOWS } from "./FlowStepCatalog";
import { SectionCardHeader } from "./FlowStepDebugUI";

const RESULT_LIMIT = 25;

/**
 * Caps the run list so the footer's "continue" button stays on screen. The list scrolls inside
 * this height instead of pushing the footer down past a long result set.
 */
const RUN_LIST_MAX_HEIGHT = 420;
const SEARCH_DEBOUNCE_MS = 300;
const ANY_USER_ID = "";

export type RunPickerFilters = {
    days: number;
    subjectUserId?: string;
    search: string;
};

export const DEFAULT_RUN_PICKER_FILTERS: RunPickerFilters = {
    days: 7,
    subjectUserId: undefined,
    search: "",
};

/** Display name for a run's subject user; falls back to the raw id when unknown. */
export function subjectUserLabel(
    subjectUserId: string,
    usersById: Map<string, UserForSharing>,
): string {
    const user = usersById.get(subjectUserId.toLowerCase());
    if (!user) return shortId(subjectUserId);
    const name = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
    return name || user.email || user.userName;
}

function RunRowSkeleton() {
    return (
        <TableBody>
            {Array.from({ length: 4 }).map((_, index) => (
                <TableRow key={index}>
                    {Array.from({ length: 4 }).map((_cell, cellIndex) => (
                        <TableCell key={cellIndex}>
                            <Skeleton height={30} />
                        </TableCell>
                    ))}
                </TableRow>
            ))}
        </TableBody>
    );
}

export function FlowStepDebugRunPicker({
    step,
    service,
    users,
    filters,
    onFiltersChange,
    selectedRun,
    onSelectRun,
}: {
    step: FlowStep;
    service: KboFlowDebugService;
    users: UserForSharing[];
    filters: RunPickerFilters;
    onFiltersChange: (filters: RunPickerFilters) => void;
    selectedRun?: KboFlowStepDebugRun;
    /** Called with the run row so the page can render it in the footer summary. */
    onSelectRun: (run: KboFlowStepDebugRun | undefined) => void;
}) {
    const theme = useTheme();
    const [runs, setRuns] = useState<KboFlowStepDebugRun[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const [truncated, setTruncated] = useState(false);
    const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

    const usersById = useMemo(
        () => new Map(users.map((user) => [user.userId.toLowerCase(), user])),
        [users],
    );

    const userOptions = useMemo(
        () => [
            { userId: ANY_USER_ID, label: "Any user" },
            ...users.map((user) => ({
                userId: user.userId,
                label:
                    `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ||
                    user.email,
            })),
        ],
        [users],
    );

    useEffect(() => {
        const timer = setTimeout(
            () => setDebouncedSearch(filters.search),
            SEARCH_DEBOUNCE_MS,
        );
        return () => clearTimeout(timer);
    }, [filters.search]);

    const phasesKey = step.phases.join(",");
    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(undefined);
        service
            .getStepDebugRuns(
                {
                    phases: phasesKey ? phasesKey.split(",") : [],
                    days: filters.days,
                    subjectUserId: filters.subjectUserId,
                    search: debouncedSearch.trim() || undefined,
                    limit: RESULT_LIMIT,
                },
                controller.signal,
            )
            .then((result) => {
                setRuns(result.runs ?? []);
                setTruncated(result.truncated ?? false);
            })
            .catch((caught: unknown) => {
                if (controller.signal.aborted) return;
                setRuns([]);
                setError(
                    caught instanceof Error
                        ? caught.message
                        : "Runs could not be loaded.",
                );
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, [
        service,
        phasesKey,
        filters.days,
        filters.subjectUserId,
        debouncedSearch,
    ]);

    useEffect(() => {
        if (loading || !selectedRun) return;
        if (!runs.some((run) => run.id === selectedRun.id)) {
            onSelectRun(undefined);
        }
    }, [loading, onSelectRun, runs, selectedRun]);

    return (
        <>
            <SectionCardHeader
                title="3 · Pick the run"
                subtitle={
                    <>
                        Completed runs that executed{" "}
                        <Box component="span" sx={{ fontWeight: 700 }}>
                            {step.label}
                        </Box>
                        .
                    </>
                }
                actions={
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1.5,
                        }}
                    >
                        <AcxSelectSingle
                            id="flow-step-debug-date-window"
                            options={RUN_DATE_WINDOWS}
                            valueField="days"
                            labelField="label"
                            width="160px"
                            isSearchable={false}
                            defaultValue={RUN_DATE_WINDOWS.find(
                                (window) => window.days === filters.days,
                            )}
                            onChange={(option?: { days: number }) =>
                                onFiltersChange({
                                    ...filters,
                                    days:
                                        option?.days ??
                                        DEFAULT_RUN_PICKER_FILTERS.days,
                                })
                            }
                        />
                        <AcxSelectSingle
                            id="flow-step-debug-user"
                            options={userOptions}
                            valueField="userId"
                            labelField="label"
                            width="180px"
                            defaultValue={userOptions[0]}
                            onChange={(option?: { userId: string }) =>
                                onFiltersChange({
                                    ...filters,
                                    subjectUserId: option?.userId || undefined,
                                })
                            }
                        />
                        <TextField
                            size="small"
                            placeholder="Search result…"
                            value={filters.search}
                            onChange={(event) =>
                                onFiltersChange({
                                    ...filters,
                                    search: event.target.value,
                                })
                            }
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchRounded fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{
                                "aria-label": "Search runs by question",
                            }}
                        />
                    </Box>
                }
            />

            {error ? (
                <Box sx={{ p: 3 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            ) : !loading && runs.length === 0 ? (
                <Box sx={{ p: 6, textAlign: "center" }}>
                    <Typography sx={{ fontWeight: 700 }}>
                        No matching runs
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        No completed run in this window executed {step.label}.
                        Widen the date range, or use manual input instead.
                    </Typography>
                </Box>
            ) : (
                <TableContainer sx={{ maxHeight: RUN_LIST_MAX_HEIGHT }}>
                    <Table
                        stickyHeader
                        aria-label="Runs that executed this step"
                        // stickyHeader defaults the header background to the theme's page
                        // colour, which reads as a seam against the white card.
                        sx={{
                            "& thead th": {
                                bgcolor: theme.palette.common.white,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 56 }} />
                                <TableCell>Question</TableCell>
                                <TableCell sx={{ width: "20%" }}>
                                    Requested for
                                </TableCell>
                                <TableCell sx={{ width: "18%" }}>
                                    Run date
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        {loading ? (
                            <RunRowSkeleton />
                        ) : (
                            <TableBody>
                                {runs.map((run) => {
                                    const isSelected =
                                        run.id === selectedRun?.id;
                                    return (
                                        <TableRow
                                            key={run.id}
                                            hover
                                            selected={isSelected}
                                            onClick={() => onSelectRun(run)}
                                            sx={{ cursor: "pointer" }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Radio
                                                    checked={isSelected}
                                                    value={run.id}
                                                    name="flow-step-debug-run"
                                                    inputProps={{
                                                        "aria-label": `Select run ${shortId(
                                                            run.runId ?? run.id,
                                                        )}`,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    {run.question
                                                        ? `"${run.question}"`
                                                        : "No question recorded"}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: theme.palette
                                                            .gray[500],
                                                        fontFamily: "monospace",
                                                    }}
                                                >
                                                    run{" "}
                                                    {shortId(
                                                        run.runId ?? run.id,
                                                    )}
                                                    {" · "}
                                                    {run.stepCount} step
                                                    {run.stepCount === 1
                                                        ? ""
                                                        : "s"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {subjectUserLabel(
                                                        run.subjectUserId,
                                                        usersById,
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatRunDate(
                                                        run.startedAtUtc ??
                                                            run.createdOn,
                                                    )}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        )}
                    </Table>
                </TableContainer>
            )}

            {truncated && !loading && !error && (
                <Box sx={{ pb: 1, px: 2.5 }}>
                    <Typography variant="caption" color="text.secondary">
                        Showing matches from the most recent runs only. Narrow
                        the date range or search to reach older ones.
                    </Typography>
                </Box>
            )}
        </>
    );
}
